import React from "react";
import { Link } from "react-router-dom";
import { SEO } from '../components/SEO';
import { SEO_CONTENT } from '../data/seoContent';

const pillars = [
  { t: "Certificate of Analysis on every lot", d: "Each shipment ships with a CoA documenting purity, identity, and key impurity data - so what you receive is verifiable, not just claimed." },
  { t: "Electronics-grade purity", d: "Trace metals are controlled to the levels OLED and semiconductor processes demand - down to sub-50 ppb on key elements, verified by ICP-MS/OES and documented on every lot. The purification know-how behind it is proprietary." },
  { t: "Analytical verification", d: "In-house HPLC/UPLC, GC-MS and LC-MS characterize identity and purity at every critical step, from intermediate to final material." },
  { t: "Full batch traceability", d: "Retained samples and batch records let us trace any lot end-to-end - essential for qualification and change control." },
];

const layers = [
  { n: "01", t: "In-house science", d: "Custom synthesis, in-house purification to electronics-grade purity, and full analytics - run in our Seoul R&D lab." },
  { n: "02", t: "Audited partner network", d: "Commercial volumes through vetted, ISO-aligned and customer-audited manufacturers, qualified via our technical audit." },
  { n: "03", t: "Embedded oversight", d: "Our engineers manage tech transfer and scale-up with the partner, de-risking every batch." },
  { n: "04", t: "Quality & compliance", d: "A Certificate of Analysis on every lot, full analytics (HPLC / GC-MS / ICP-MS), and end-to-end traceability." },
];

const stats = [
  ["ISO 9001 / 14001 / 45001", "Partner quality systems"],
  ["Customer-audited", "Partner facilities"],
  ["g to multi-tonne", "Synthesis scale"],
  ["< 50 ppb", "Metal impurities"],
];

const partnerSpecs = [
  ["Quality system", "ISO 9001 / 14001 / 45001 aligned, regularly customer-audited"],
  ["Specialties", "OLED emitters (fluorescent / phosphorescent / TADF), HTL/ETL/HIL/EIL transport & host materials, organometallics (Ir/Pt/Pd), organic-semiconductor monomers & oligomers, photoresist intermediates"],
  ["Synthesis", "Gram to multi-tonne; 20+ reaction types incl. Suzuki, Ullmann, Stille, Buchwald-Hartwig couplings, C-H activation, organolithium, cryogenic to high-temperature"],
  ["Purification", "Multi-stage purification to electronics-grade purity; inert-atmosphere handling and cleanroom packaging"],
  ["Analytics", "HPLC, LC-MS, GC-MS, NMR, ICP-MS / ICP-OES (trace metals), PL / fluorescence, DSC / TGA"],
];

const auditPoints = [
  "Quality management system & documentation",
  "Equipment qualification & calibration",
  "Process controls & batch records",
  "Material handling & contamination control",
  "Environmental, health & safety practices",
  "Regulatory licensing & permits",
];

export default function Quality() {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...SEO_CONTENT.quality} />
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-800 text-white py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Quality &amp; Compliance</h1>
          <p className="text-teal-400 text-lg md:text-xl">
            Electronics-grade quality you can verify - backed by analytics, audited partners, and protected IP.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Our quality system</h2>
        <p className="text-slate-600 mb-10 max-w-3xl">
          Quality is built in at the bench, not inspected in at the end. We control synthesis, purification, and analytics in-house, so every material is characterized and documented before it ships.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          {pillars.map((p) => (
            <div key={p.t} className="bg-slate-50 border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-slate-900 mb-2">{p.t}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-4">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">How our network works</h2>
        <p className="text-slate-600 mb-10 max-w-3xl">
          We own the high-value science and the quality system, and wrap a documented, audited process around a qualified manufacturing network - so you get scale without the overhead, and oversight without the compromise.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {layers.map((l) => (
            <div key={l.n} className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <div className="text-teal-600 font-bold text-xl mb-2">{l.n}</div>
              <h3 className="font-semibold text-slate-900 mb-1">{l.t}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{l.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-900 text-white py-16 px-6 mt-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">A vetted, audited partner network</h2>
          <p className="text-slate-300 mb-8 max-w-3xl">
            We scale through selected manufacturers, qualified through a structured technical audit and held to our standard. Partner identities are protected under NDA - the same confidentiality we extend to your IP.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            {stats.map(([big, small]) => (
              <div key={small} className="text-center">
                <div className="font-mono text-teal-400 text-base md:text-lg">{big}</div>
                <div className="text-slate-400 text-sm mt-1">{small}</div>
              </div>
            ))}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-teal-400 bg-teal-400/10 rounded px-2 py-1">Audited partner</span>
              <span className="text-sm text-slate-300">OLED &amp; electronic materials (CDMO) &middot; East Asia</span>
            </div>
            <dl className="space-y-4">
              {partnerSpecs.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-teal-400 text-sm font-medium">{k}</dt>
                  <dd className="text-slate-200 text-sm leading-relaxed mt-0.5">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <p className="text-slate-400 text-xs mt-4">Representative of our onboarded network. Additional audited partners are being added; full audit records are available to qualified customers under NDA.</p>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200 py-16 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">What our partner audit covers</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Every partner is qualified through a structured technical audit, and LAMKO stays embedded in quality oversight through scale-up and ongoing supply.
            </p>
            <p className="text-slate-600 leading-relaxed">
              You get manufacturing capacity without the overhead, and our oversight without the compromise.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <ul className="space-y-2">
              {auditPoints.map((a) => (
                <li key={a} className="flex gap-3 text-sm text-slate-600">
                  <span className="text-teal-600 font-bold">&#10003;</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-3">Compliance posture</h3>
            <p className="text-slate-700 text-sm leading-relaxed">
              Our quality system is designed around ICH Q7, Q9 and Q10 and ISO 9001 / 14001 / 45001 principles, across our own lab and our audited partner network. We are not yet independently certified; customers typically qualify us through their own audit and questionnaire, and formal certification is on our roadmap.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200 py-16 px-6"><div className="max-w-6xl mx-auto"><h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Documented quality system</h2><p className="text-slate-600 mb-8 max-w-3xl">The hard part is not making it once, it is making it the same way every time. LAMKO operates a documented quality system of over 200 standard operating procedures across Quality Assurance, Quality Control, Production, EHS, Cleaning, Warehouse, and Supply Chain - designed around ICH Q7, Q9 and Q10 and ISO 9001 / 14001 / 45001 principles, with USP-, NFPA- and OSHA-aligned procedures. SOPs are version-controlled and govern both our Seoul lab and our audited manufacturing partners.</p><div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8"><div className="bg-white border border-slate-200 rounded-xl p-5 text-center"><div className="font-mono text-teal-600 text-2xl font-bold">200+</div><div className="text-slate-600 text-sm mt-1">Controlled SOPs</div></div><div className="bg-white border border-slate-200 rounded-xl p-5 text-center"><div className="font-mono text-teal-600 text-2xl font-bold">7</div><div className="text-slate-600 text-sm mt-1">Functional domains</div></div><div className="bg-white border border-slate-200 rounded-xl p-5 text-center"><div className="font-mono text-teal-600 text-xl font-bold">Quarterly</div><div className="text-slate-600 text-sm mt-1">SOP review cycle</div></div><div className="bg-white border border-slate-200 rounded-xl p-5 text-center"><div className="font-mono text-teal-600 text-xl font-bold">Self-inspection</div><div className="text-slate-600 text-sm mt-1">Internal audit</div></div></div><div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-wrap items-center justify-between gap-4"><div className="max-w-xl"><h3 className="font-semibold text-slate-900 mb-1">Quality documentation</h3><p className="text-slate-600 text-sm">A Quality System Overview and the full Quality Dossier (the SOPs in scope for your audit) are shared under a mutual NDA. We also respond to standard supplier quality questionnaires.</p></div><Link to="/get-in-touch" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg px-6 py-3 transition-colors whitespace-nowrap">Request under NDA &rarr;</Link></div></div></section>

      <section className="bg-slate-50 border-t border-slate-200 py-14 px-6 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Need our quality documentation?</h2>
        <p className="text-slate-600 mb-6 max-w-2xl mx-auto">We&apos;re happy to walk qualified customers through our quality system, sample CoAs, and partner-audit approach.</p>
        <Link to="/get-in-touch" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg px-8 py-3 transition-colors">
          Talk to our team &rarr;
        </Link>
      </section>
    </div>
  );
}
